import { useAppSelector } from '@crm-src/lib/store'

// Define the hook function
const usePermissions = () => {
  const permissions = useAppSelector((state) => state.auth.permissions) || []
  const user = useAppSelector((state) => state.auth.user)
  
  // Check if user has a specific permission (backwards compatibility)
  const hasPermission = (resource: string, action: string): boolean => {
    if (!permissions || !Array.isArray(permissions)) return false
    const permissionString = `${resource}:${action}`
    return permissions.includes(permissionString)
  }
  
  // Check if user has page access (new page-based permissions)
  const canAccessPage = (page: string): boolean => {
    // Ensure permissions is an array
    if (!permissions || !Array.isArray(permissions)) return false
    
    // Special case: Admin users should have access to all pages
    // This handles cases where permissions might not be cached yet
    const hasAdminRole = user?.roles && user.roles.some((role: { id: number; name: string; description: string }) =>
      role.name === 'admin' || role.name === 'Admin'
    )
    const hasAdminPermission = permissions.includes('admin:access')
    
    if (hasAdminRole || hasAdminPermission) {
      return true
    }
    
    // Handle both formats: "resellers:access" (full permission string) or "resellers" (page name)
    const permissionToCheck = page.includes(':') ? page : `${page}:access`
    
    return permissions.includes(permissionToCheck)
  }
  
  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionsList: string[]): boolean => {
    if (!permissions || !Array.isArray(permissions)) return false
    return permissionsList.some(permission => permissions.includes(permission))
  }
  
  // Page-based permission checks
  const canAccessTasks = (): boolean => {
    return canAccessPage('tasks')
  }
  
  const canAccessProspects = (): boolean => {
    return canAccessPage('prospects')
  }
  
  const canAccessCustomers = (): boolean => {
    return canAccessPage('customers')
  }
  
  const canAccessContacts = (): boolean => {
    return canAccessPage('contacts')
  }
  
  const canAccessEmailCampaigns = (): boolean => {
    return canAccessPage('email_campaigns')
  }
  
  const canAccessInvoices = (): boolean => {
    return canAccessPage('invoices')
  }
  
  const canAccessFileManager = (): boolean => {
    return canAccessPage('file_manager')
  }
  
  const canAccessResellers = (): boolean => {
    return canAccessPage('resellers')
  }

  // Reseller APPLICATIONS — the companies asking to BECOME resellers, reviewed
  // on the second tab of Reseller Management. Its own grant (the dashboard's
  // `crm:reseller_applications`, super_admin baseline, grantable per role) and
  // NOT implied by canAccessResellers: a sales manager works with the resellers
  // that exist and does not decide who joins. Gates the tab + the review route;
  // crm-api enforces the same authority through the crm_reseller_applications
  // claim, so hiding the tab is a courtesy, not the security.
  const canAccessResellerApplications = (): boolean => {
    return canAccessPage('reseller_applications')
  }

  // Licences: the page that builds a licence for one appliance and issues the
  // signed file a customer installs. ISSUING additionally needs crm:edit, which
  // the dashboard BFF enforces — this only gates VISIBILITY, like every other
  // canAccessPage check.
  const canAccessLicences = (): boolean => {
    return canAccessPage('licences')
  }

  // Releases + Pricing: the release folders (each version's change log, Disk 1
  // / Disk 2, documentation and download journal), the product catalogue and
  // the XOP price list — two sidebar entries, ONE grant (`crm:products_pricing`
  // → this string). Gates VISIBILITY only; curating anything on either needs
  // `admin:users`, which the dashboard BFF enforces and the pages mirror with
  // `useCan`.
  const canAccessProductsPricing = (): boolean => {
    return canAccessPage('products_pricing')
  }

  // May APPROVE or REJECT a licence longer than a 30-day trial. Super admins
  // and nobody else — the dashboard emits `licences:approve` only for that role
  // (server/src/auth/crmSessionPermissions.ts: canApproveCrmLicences) and
  // crm-api enforces the same authority through the crm_licences_approve claim.
  const canApproveLicences = (): boolean => {
    return isAdmin() || permissions.includes('licences:approve')
  }

  // May create, edit or issue a licence for one of XOP's OWN appliances. Super
  // admins and nobody else — there is deliberately no grant (the dashboard emits
  // `internal_licences:manage` for that role alone, see
  // server/src/auth/crmSessionPermissions.ts: canManageInternalLicences).
  //
  // THIS ONLY DECIDES WHAT IS OFFERED. It reveals the "XOP Networks Inc." entry
  // in the New licence dialog; crm-api reads the matching crm_internal_licences
  // claim strictly and is the only thing that decides what is allowed.
  const canManageInternalLicences = (): boolean => {
    return isAdmin() || permissions.includes('internal_licences:manage')
  }

  // Reseller WRITE capability — gates the Resellers page's create/edit/delete
  // controls (distinct from canAccessResellers, which only gates VISIBILITY).
  // The dashboard emits the `resellers:manage` string only for an actor holding
  // BOTH crm:resellers AND crm:resellers_edit (server/src/auth/
  // crmSessionPermissions.ts: canManageCrmResellers), so a sales_manager manages
  // its OWN resellers while a read-only reseller viewer stays view-only.
  // super_admin bypasses via admin:access like every other check. crm-api
  // independently enforces it (crm_resellers_edit claim) + own-scope on the API.
  const canManageResellers = (): boolean => {
    return isAdmin() || permissions.includes('resellers:manage')
  }

  // May ATTACH / REMOVE our salespeople on a reseller (2026-10-02) — the
  // Assigned Salespeople card's controls and the salespeople picker in the
  // reseller form. It used to be isAdmin(); now the dashboard emits
  // `resellers:assign_salespeople` for crm:resellers + crm:resellers_salespeople
  // (canAssignCrmResellerSalespeople), so an admin can hand it to one person.
  // Independent of canManageResellers. crm-api enforces it on its own through
  // the strict crm_resellers_salespeople claim + the own-scope guard.
  const canAssignResellerSalespeople = (): boolean => {
    return isAdmin() || permissions.includes('resellers:assign_salespeople')
  }
  
  const canAccessFinancialMetrics = (): boolean => {
    return canAccessPage('financial_metrics')
  }

  // The financial wall. `financials:access` is granted only to a holder of the
  // dashboard `crm:financials` permission (or super_admin), and it is the SINGLE
  // gate every monetary surface in the CRM consults — invoice amounts, customer
  // MRR/LTV, prospect deal value, agreement/equipment prices, scheduled-invoice
  // totals, the revenue dashboards. When false, money is redacted EVERYWHERE,
  // not just by hiding the Invoices page. Prefer the <Money> component / the
  // useFinancialAccess hook (components/money) over calling this directly so the
  // redaction stays consistent. `admin:access` (super_admin) bypasses, same as
  // every other page check.
  const canViewFinancials = (): boolean => {
    return canAccessPage('financials')
  }
  
  const canAccessAdmin = (): boolean => {
    return canAccessPage('admin')
  }
  
  const canAccessSuperAdmin = (): boolean => {
    return canAccessPage('super_admin')
  }
  
  // Role-based checks
  const isAdmin = (): boolean => {
    return canAccessAdmin()
  }

  const isSuperAdmin = (): boolean => {
    return canAccessSuperAdmin()
  }

  return {
    hasPermission,
    canAccessPage,
    hasAnyPermission,
    canAccessTasks,
    canAccessProspects,
    canAccessCustomers,
    canAccessContacts,
    canAccessEmailCampaigns,
    canAccessInvoices,
    canAccessFileManager,
    canAccessResellers,
    canAccessResellerApplications,
    canManageResellers,
    canAssignResellerSalespeople,
    canAccessLicences,
    canAccessProductsPricing,
    canApproveLicences,
    canManageInternalLicences,
    canAccessFinancialMetrics,
    canViewFinancials,
    canAccessAdmin,
    canAccessSuperAdmin,
    isAdmin,
    isSuperAdmin,
    permissions
  }
}

// Export both named and default for compatibility
export { usePermissions }
export default usePermissions
